/* ===========================================================
   Loja sintética para impersonação. A demo tem UM StoreData real
   (Koloni Skate Shop); para o operador conseguir "entrar como" qualquer
   conta de forma consistente, derivamos a loja reescalando a demo
   por um fator e trocando nome/segmento.

   Por que reescalar UNIFORMEMENTE preserva a coerência: todos os
   invariantes do mock são lineares (waterfall soma o lucro, canais
   somam a receita, pedidos ≈ sessões × conversão). Multiplicar toda
   quantidade ADITIVA pelo mesmo fator e deixar as RAZÕES intactas
   (margem, ROAS, conversão, ticket, preços de plano) mantém as somas
   e proporções. É simplificação de demo — não vira regra de banco
   (database-schema.md §6). Em produção cada tenant tem dados próprios.
   =========================================================== */
import type { StoreData } from "../api/types";
import { faixaParaFaturamento } from "../domain/faixa";
import { KOLONI_SKATESHOP } from "./koloni-skateshop";
import type { TenantRegistryEntry } from "./tenant-registry";

/** Chaves que NÃO escalam: razões, taxas, valores por unidade, dias, ids,
 *  eixos de série e o catálogo de planos (preço é nível de produto). */
const NAO_ESCALAR = new Set([
  "margem",
  "be",
  "conv",
  "conversao",
  "conversaoSegmento",
  "conversaoTop10",
  "roasReal",
  "roasPlat",
  "roasMedio",
  "roasInformado",
  "cpa",
  "mer",
  "estoqueDias",
  "intervaloDias",
  "margemLiquida",
  "deltaLucro",
  "deltaReceita",
  "deltaConversao",
  "deltaFreteMedio",
  "ticketMedio",
  "preco",
  "precoMensal",
  "faturamentoMin",
  "faturamentoMax",
  "acessosInclusos",
  "precoAcessoAdicional",
  "acessosAdicionais",
  "impostoDefault",
  "rateioFixo",
  "cmvEstimadoPct",
  "imposto",
  "embalagem",
  "cmv",
  "dia",
  "ltv",
  "recompra",
  "id",
]);

function escalar(value: unknown, key: string | null, fator: number): unknown {
  if (typeof value === "number") {
    return key && !NAO_ESCALAR.has(key) ? Math.round(value * fator) : value;
  }
  if (Array.isArray(value)) {
    return value.map((v) =>
      typeof v === "number"
        ? key && !NAO_ESCALAR.has(key)
          ? Math.round(v * fator)
          : v
        : escalar(v, null, fator),
    );
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = escalar(v, k, fator);
    return out;
  }
  return value;
}

const cache = new Map<string, StoreData>();

/** Invalida o cache de uma loja sintética (ou tudo). Usado quando a persona
 *  de demo troca de faixa: o mesmo storeId ("demo-lead") precisa reescalar. */
export function clearSyntheticStoreCache(storeId?: string): void {
  if (storeId) cache.delete(storeId);
  else cache.clear();
}

export function syntheticStore(meta: TenantRegistryEntry): StoreData {
  const cached = cache.get(meta.tenant.storeId);
  if (cached) return cached;

  const store = escalar(KOLONI_SKATESHOP, null, meta.sizeFactor) as StoreData;

  // Catálogo de preços é nível de produto — igual para todo tenant. A faixa,
  // não: re-detecta pelo faturamento reescalado e a assinatura segue o preço
  // da faixa re-detectada (decisions.md §20).
  store.plano = structuredClone(KOLONI_SKATESHOP.plano);
  const faixa = faixaParaFaturamento(
    store.plano.faixas,
    store.faixaDetectada.faturamentoMensalMedio,
  );
  store.faixaDetectada.faixaId = faixa.id;
  store.assinatura = {
    ...structuredClone(KOLONI_SKATESHOP.assinatura),
    faixaId: faixa.id,
    precoMensal: faixa.precoMensal,
    historicoPagamentos: KOLONI_SKATESHOP.assinatura.historicoPagamentos.map((p) => ({
      ...p,
      id: `pg-${faixa.id}-${p.data.slice(0, 7)}`,
      valor: faixa.precoMensal,
    })),
  };

  // Identidade da loja impersonada.
  store.nuvemshop.loja = meta.name;
  store.nuvemshop.segmento = meta.segment;
  // ticketMedio não escala; reafirma a relação receita/pedidos pós-escala.
  store.nuvemshop.ticketMedio = store.nuvemshop.pedidos
    ? store.nuvemshop.receita / store.nuvemshop.pedidos
    : 0;
  // Recomendação genérica: a copy original cita produtos/valores da Koloni, que
  // não fazem sentido numa loja com outro nome (impersonação e demo do lead).
  // Mantém o gancho ("tem dinheiro a recuperar") sem amarrar ao catálogo demo.
  store.derivados.respostaDoDia =
    "Há produtos perto de zerar o estoque e campanhas abaixo do ponto de equilíbrio. Abra o Diagnóstico para ver onde recuperar margem agora.";

  cache.set(meta.tenant.storeId, store);
  return store;
}
